import axios from 'axios'
import useJwt from '@/auth/jwt/useJwt'
import { initialAbility } from '@/libs/acl/config'
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import mainBaseUrl from '@/global-components'

class WebServiceRequest {
  static baseUrl = mainBaseUrl

  static baseSocketUrl = mainBaseUrl

  static baseSucketUrl = mainBaseUrl

  constructor(context = null) {
    this.context = context
    this.data = {}
    this.queryUrl = {}
    this.formData = null
    this.method = 'POST'
    this.service = ''
    this.tag = ''
    this.userToken = this.getAccessToken()
  }

  static normalizeUrlPart(value) {
    if (value === null || value === undefined) {
      return ''
    }

    return String(value)
        .trim()
        .replace(/^\/+|\/+$/g, '')
  }

  static joinUrl(...parts) {
    const validParts = parts
        .filter(part => (
            part !== null
            && part !== undefined
            && String(part).trim() !== ''
        ))
        .map((part, index) => {
          const value = String(part).trim()

          if (index === 0) {
            return value.replace(/\/+$/g, '')
          }

          return WebServiceRequest.normalizeUrlPart(value)
        })

    return validParts.join('/')
  }

  static getNotifyHubUrl() {
    return WebServiceRequest.joinUrl(
        WebServiceRequest.baseSocketUrl,
        'hub',
        'NotifyHub',
    )
  }

  getAccessToken() {
    const token = useJwt.getToken()

    if (!token) {
      return ''
    }

    return String(token)
        .replace(/^Bearer\s+/i, '')
        .replace(/^"(.*)"$/, '$1')
        .trim()
  }

  getData() {
    return this.data
  }

  setData(data) {
    this.data = data === undefined ? {} : data

    return this
  }

  setRequestBody(value) {
    this.data = value

    return this
  }

  setTag(tag) {
    this.tag = tag || ''

    return this
  }

  getTag() {
    return this.tag
  }

  setMethod(method) {
    this.method = method
        ? String(method).toUpperCase()
        : 'POST'

    return this
  }

  setService(service) {
    this.service = service || ''

    return this
  }

  setRequestParam(value = {}) {
    if (
        !value
        || typeof value !== 'object'
        || Array.isArray(value)
    ) {
      this.queryUrl = {}

      return this
    }

    const cleanedValue = {
      ...value,
    }

    Object.keys(cleanedValue).forEach(key => {
      const item = cleanedValue[key]

      if (
          item === null
          || item === undefined
          || item === ''
      ) {
        delete cleanedValue[key]
      }
    })

    this.queryUrl = cleanedValue

    return this
  }

  convertDataToQueryString() {
    const searchParams = new URLSearchParams()

    Object.keys(this.queryUrl || {}).forEach(key => {
      const value = this.queryUrl[key]

      if (
          value === null
          || value === undefined
          || value === ''
      ) {
        return
      }

      if (Array.isArray(value)) {
        value.forEach(item => {
          if (
              item !== null
              && item !== undefined
              && item !== ''
          ) {
            searchParams.append(key, String(item))
          }
        })

        return
      }

      if (
          typeof value === 'object'
          && !(value instanceof Date)
      ) {
        searchParams.append(
            key,
            JSON.stringify(value),
        )

        return
      }

      searchParams.append(
          key,
          String(value),
      )
    })

    return searchParams.toString()
  }

  setRequestParamDataObj(value = {}) {
    if (
        !value
        || typeof value !== 'object'
        || Array.isArray(value)
    ) {
      this.data = {}

      return this
    }

    const deepCloned = JSON.parse(
        JSON.stringify(value),
    )

    Object.keys(deepCloned).forEach(key => {
      const item = deepCloned[key]

      if (
          item === null
          || item === undefined
          || item === ''
      ) {
        delete deepCloned[key]
      }
    })

    this.data = deepCloned

    return this
  }

  setFormData(data) {
    this.formData = data

    return this
  }

  getFormData() {
    return this.formData
  }

  getUrl() {
    const baseEndpoint = WebServiceRequest.joinUrl(
        WebServiceRequest.baseUrl,
        this.service,
        'api',
        'v1',
        this.getTag(),
    )

    const queryString = this.convertDataToQueryString()

    if (!queryString) {
      return baseEndpoint
    }

    return `${baseEndpoint}?${queryString}`
  }

  getRequestHeaders(isFormData = false) {
    const token = this.getAccessToken()

    const headers = {
      Accept: 'application/json',
    }

    if (!isFormData) {
      headers['Content-Type'] = 'application/json'
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    return headers
  }

  showToast({
              title = 'Alert',
              text = '',
              variant = 'danger',
              icon = '',
            }) {
    if (
        !this.context
        || typeof this.context.$toast !== 'function'
    ) {
      return
    }

    this.context.$toast({
      component: ToastificationContent,
      position: 'bottom-center',
      props: {
        title,
        icon,
        variant,
        text,
      },
    })
  }

  resetAuthentication() {
    localStorage.removeItem(
        useJwt.jwtConfig.storageTokenKeyName,
    )

    localStorage.removeItem(
        useJwt.jwtConfig.storageRefreshTokenKeyName,
    )

    localStorage.removeItem('userData')

    if (
        this.context
        && this.context.$ability
        && typeof this.context.$ability.update === 'function'
    ) {
      this.context.$ability.update(initialAbility)
    }
  }

  redirectToLogin() {
    if (
        !this.context
        || !this.context.$router
    ) {
      return
    }

    if (
        this.context.$route
        && this.context.$route.name === 'auth-login'
    ) {
      return
    }

    this.context.$router
        .push({
          name: 'auth-login',
        })
        .catch(error => {
          if (
              !error
              || error.name !== 'NavigationDuplicated'
          ) {
            console.error(
                '[WebService] Login redirect failed:',
                error,
            )
          }
        })
  }

  getErrorMessage(error) {
    const responseData = error && error.response
        ? error.response.data
        : null

    if (typeof responseData === 'string') {
      return responseData
    }

    if (responseData && responseData.message) {
      return responseData.message
    }

    if (responseData && responseData.Message) {
      return responseData.Message
    }

    if (error && error.message) {
      return error.message
    }

    return 'Something went wrong. Please try again.'
  }

  handleHttpError(error) {
    const response = error && error.response
        ? error.response
        : null

    if (!response) {
      this.showToast({
        title: 'Connection error',
        text: 'Unable to connect to the server. Please check your internet connection.',
      })

      return
    }

    const { status } = response
    const message = this.getErrorMessage(error)

    switch (status) {
      case 400:
        this.showToast({
          title: 'Warning',
          text: message,
        })
        break

      case 401:
        this.resetAuthentication()
        this.redirectToLogin()
        break

      case 403:
        this.showToast({
          title: 'Access denied',
          text: message || 'You do not have permission to perform this action.',
        })
        break

      case 404:
        this.showToast({
          title: 'Not found',
          text: message || 'The requested resource was not found.',
        })
        break

      case 408:
        this.showToast({
          title: 'Request timeout',
          text: 'The request took too long. Please try again.',
        })
        break

      case 422:
        this.showToast({
          title: 'Validation error',
          text: message,
        })
        break

      case 500:
        this.showToast({
          title: 'Server error',
          text: 'Something went wrong. Please try again later.',
        })
        break

      case 502:
      case 503:
      case 504:
        this.showToast({
          title: 'Service unavailable',
          text: 'The service is temporarily unavailable. Please try again later.',
        })
        break

      default:
        this.showToast({
          title: 'Alert',
          text: message,
        })
        break
    }
  }

  finishLoading() {
    if (!this.context) {
      return
    }

    if ('overlay' in this.context) {
      this.context.overlay = false
    }

    if ('showOverlay' in this.context) {
      this.context.showOverlay = false
    }
  }

  callCallback(callback, data) {
    if (typeof callback === 'function') {
      callback(data)
    }
  }

  async request({
                  data,
                  isFormData = false,
                  successCallback,
                  errorCallback,
                }) {
    try {
      const response = await axios({
        url: this.getUrl(),
        data,
        method: this.method,
        headers: this.getRequestHeaders(isFormData),
        withCredentials: true,
      })

      const responseData = response && response.data !== undefined
          ? response.data
          : response

      const hasSuccessFlag = (
          responseData
          && typeof responseData === 'object'
          && Object.prototype.hasOwnProperty.call(
              responseData,
              'isSuccess',
          )
      )

      const isSuccessful = (
          !hasSuccessFlag
          || responseData.isSuccess === true
      )

      if (isSuccessful) {
        this.callCallback(
            successCallback,
            responseData,
        )

        return responseData
      }

      this.callCallback(
          errorCallback,
          responseData,
      )

      return responseData
    } catch (error) {
      console.error(
          '[WebService] Request failed:',
          {
            url: this.getUrl(),
            method: this.method,
            error,
          },
      )

      this.handleHttpError(error)

      this.callCallback(
          errorCallback,
          error.response && error.response.data
              ? error.response.data
              : error,
      )

      return null
    } finally {
      this.finishLoading()
    }
  }

  async fetch(successCallback, errorCallback) {
    return this.request({
      data: this.getData(),
      isFormData: false,
      successCallback,
      errorCallback,
    })
  }

  async sendFormData(
      successCallback,
      errorCallback,
  ) {
    return this.request({
      data: this.getFormData(),
      isFormData: true,
      successCallback,
      errorCallback,
    })
  }
}

export default WebServiceRequest
